import { useState } from "react";
import { card, btnSecondary, StepBadge } from "./ui.js";

/** お客様のクラウドサインに登録してもらう Webhook の URL（routes/cloudsign-webhook.ts） */
export function webhookUrl(origin: string): string {
  return `${origin}/api/webhooks/cloudsign`;
}

/**
 * 締結完了の即時反映（クラウドサインの Webhook）の設定手順。
 *
 * Webhook の通知先は、お客様のクラウドサインの「Web API設定」でしか登録できない
 * （Web API に登録用のエンドポイントは無い）。そのため URL と手順を出して、お客様に登録してもらう。
 * 登録しなくても 15 分間隔のポーリングで反映されるので、任意の設定として案内する。
 */
export default function WebhookSetupCard() {
  const url = webhookUrl(window.location.origin);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyState("copied");
    } catch {
      // 権限やブラウザの設定でコピーできないことがある。URL は画面に出ているので手で選べる
      setCopyState("failed");
    }
  };

  const steps = [
    { num: 1, title: "クラウドサインにログイン", desc: "クライアントIDを発行したアカウントでログインします" },
    { num: 2, title: "Web API設定を開く", desc: "「Web API設定」の「Webhook」→「Webhookを追加」" },
    { num: 3, title: "URL を登録", desc: "下の URL を貼り付け、通知する条件で「締結時」「取り消し・却下時」を選んで保存" },
    { num: 4, title: "テスト送信", desc: "登録した Webhook の「テスト送信」で、エラーにならないことを確認" },
  ];

  return (
    <div style={card}>
      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>締結完了の即時反映（任意）</div>
      <div style={{ fontSize: 13, color: "#6b6b68", lineHeight: 1.7, marginBottom: 16 }}>
        クラウドサインに Webhook を登録すると、締結完了・取り消しが HubSpot にすぐ反映されます。
        登録しない場合も、15 分ごとの確認で反映されます。
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
        <code
          style={{
            flex: "1 1 280px", minWidth: 0, padding: "10px 12px", borderRadius: 8,
            border: "1px solid #e8e7e3", background: "#FAFAFE", fontSize: 13,
            wordBreak: "break-all", userSelect: "all",
          }}
          aria-label="Webhook の URL"
        >
          {url}
        </code>
        <button onClick={copy} style={{ ...btnSecondary, fontSize: 13, padding: "8px 16px" }}>
          URL をコピー
        </button>
      </div>
      <div role="status" aria-live="polite" style={{ fontSize: 12, color: copyState === "failed" ? "#A32D2D" : "#6b6b68", minHeight: 18, marginBottom: 12 }}>
        {copyState === "copied" && "コピーしました"}
        {copyState === "failed" && "コピーできませんでした。URL を選択してコピーしてください"}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12, fontSize: 14, color: "#4a4a48" }}>
        {steps.map((s) => (
          <div key={s.num} style={{ display: "flex", alignItems: "flex-start" }}>
            <StepBadge num={s.num} done={false} />
            <div>
              <span style={{ fontWeight: 600 }}>{s.title}</span><br />
              <span style={{ color: "#6b6b68", fontSize: 13 }}>{s.desc}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
